
import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Button,
    Divider,
    Paper,
    Alert,
    Container
} from '@mui/material';
import { useOktaAuth } from "@okta/okta-react";
import { useLocation, useParams } from "react-router-dom";
import AxiosInstance from '../configs/axiosConfig';
import { useSnackbar } from '../contexts/SnackbarContext';

import Header from "../components/Header";
import Footer from "../components/Footer";

/**
  * MarloLabsForm (API-field aligned)
  * ---------------------------------------------------------------------------
  * - Captures `userid` from route param (e.g., /patients/:userid/form)
  * - Posts EXACT API keys provided by you (1..43)
  * - Uses only deps you already have in package.json
  */

// ----------------------------- Types -----------------------------
type YesNo = 'Yes' | 'No';
type Gender = 'Male' | 'Female' | 'Other';
type Severity = 'None' | 'Suspect' | 'Mild' | 'Moderate' | 'High';
type AddPower = 'LOW' | 'MEDIUM' | 'HIGH';

interface ApiPayload {
    // 1  userid (from route param)
    userid: string;

    // 2..6 OD Rx
    ODsphereRx: number | '';
    ODcylinderRx: number | '';
    ODaxis: number | '';
    ODaddPower: number | '';
    ODreadingPresciption: number | ''; // spelled exactly as provided

    // 7..11 OS Rx
    OSsphereRx: number | '';
    OScylinderRx: number | '';
    OSaxis: number | '';
    OSaddPower: number | '';
    OSreadingPresciption: number | ''; // spelled exactly as provided

    // 12..13 Demographics
    age: number | '';
    gender: Gender | '';

    // 14..21 OD/OS measurements
    ODhvid: number | '';
    OShvid: number | '';
    ODksteep: string;
    OSksteep: string;
    ODkflat: string;
    OSkflat: string;
    ODpupilSize: number | '';
    OSpupilSize: number | '';

    // 22..25 Anterior eye
    AEdryEyeIssue: Severity | '';
    AEIrregularCornea: Severity | '';
    AErce: Severity | '';
    AEother: string;

    // 26..29 Final MF & CLs
    finalMFlensPurchased: string;
    finalMFlensPurchasedNotes: string;
    finalMFPower: AddPower | '';
    finalMFPowerNotes: string;
    CLs: string;
    CLNotes: string;

    // 30..33 Existing MF history
    existingMFpurchased: string;
    existedMFpurchasedNotes: string;
    existedMFPower: AddPower | '';
    existedMFPowerNotes: string;

    // 34..35 New lens tries
    newLensTriedBeforePurchasing: number | '';
    newLensTriedBeforePurchasingNotes: string;

    tryDifferentLenses: string;
    tryDifferentLensesNotes: string;

    // 36..39 Surgery history
    priorRefractiveSurgery: YesNo | '';
    priorRefractiveSurgeryNotes: string;
    priorCataractSurgery: YesNo | '';
    priorCataractSurgeryNotes: string;

    // 40..43 Other flags
    OTCeyeProducts: YesNo | '';
    hoursWearingCLs: number | '';
    typeApersonality: YesNo | '';
    postFitSatisfaction: number | ''; // (1–5), API field #43
}

// --------------------------- Dropdowns ---------------------------
// Axis: 0..180 in 10° steps
const AXIS_OPTIONS: number[] = [
    0, 10, 20, 30, 40, 50,
    60, 70, 80, 90, 100, 110,
    120, 130, 140, 150, 160, 170, 180,
];

// Lens powers per template (−15 .. +8 incl. quarter steps where applicable)
const LENS_POWERS: number[] = [
    -15, -14.5, -14, -13.5, -13, -12.5, -12, -11.5, -11, -10.5, -10,
    -9.75, -9.5, -9.25, -9, -8.75, -8.5, -8.25, -8, -7.75, -7.5, -7.25, -7,
    -6.75, -6.5, -6.25, -6, -5.75, -5.5, -5.25, -5, -4.75, -4.5, -4.25, -4,
    -3.75, -3.5, -3.25, -3, -2.75, -2.5, -2.25, -2, -1.75, -1.5, -1.25, -1,
    -0.75, -0.5, -0.25, 0, 0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2, 2.25,
    2.5, 2.75, 3, 3.25, 3.5, 3.75, 4, 4.25, 4.5, 4.75, 5, 5.25, 5.5, 5.75, 6,
    6.5, 7, 7.5, 8,
];

// Cylinder Rx options (−3.00 .. 0 in 0.25 increments)
const CYLINDER_POWERS: number[] = [
  -3.0, -2.75, -2.5, -2.25, -2.0, -1.75, -1.5, -1.25, -1.0, -0.75, -0.5, -0.25, 0,
];

// ADD / Reading Rx options (+0.75 .. +3.50 in +0.25)
const ADD_POWERS: number[] = [
  0.75, 1.0, 1.25, 1.5, 1.75, 2.0, 2.25, 2.5, 2.75, 3.0, 3.25, 3.5,
];

const YES_NO: YesNo[] = ['Yes', 'No'];
const GENDER: Gender[] = ['Male', 'Female', 'Other'];
const SEVERITY: Severity[] = ['None', 'Suspect', 'Mild', 'Moderate', 'High'];
const ADD_POWER: AddPower[] = ['LOW', 'MEDIUM', 'HIGH'];

const NON_MULTIFOCAL_CLS: string[] = [
    'NONE',
    'AIR OPTIX® AQUA',
    'AIR OPTIX® COLORS',
    'AIR OPTIX® NIGHT & DAY® AQUA',
    'AIR OPTIX® for Astigmatism',
    'AIR OPTIX® plus HydraGlyde®',
    'AIR OPTIX® plus HydraGlyde® for Astigmatism',
    'DAILIES TOTAL1®',
    'DAILIES TOTAL1® for Astigmatism',
    'DAILIES® AquaComfort Plus®',
    'DAILIES® AquaComfort Plus® Toric',
    'DAILIES® COLORS',
    'FOCUS® DAILIES®',
    'FreshLook® COLORBLENDS®',
    'FreshLook® COLORS',
    'FreshLook® DIMENSIONS',
    'FreshLook® Handling Tint',
    'PRECISION1®',
    'PRECISION1® for Astigmatism',
    'TOTAL30®',
    'TOTAL30® for Astigmatism',
    'NON-ALCON Spherical',
    'NON-ALCON Toric',
    'NON-ALCON Other',
];

const MULTIFOCAL_CLS: string[] = [
  'NONE',
  'AIR OPTIX® AQUA Multifocal',
  'AIR OPTIX® plus HydraGlyde® Multifocal',
  'DAILIES TOTAL1® Multifocal',
  'DAILIES® AquaComfort Plus® Multifocal',
  'TOTAL30® Multifocal',
  'NON-ALCON Multifocal',
  'NON-ALCON Toric Multifocal',
];

// --------------------------- Helpers -----------------------------
const renderNumberMenuItems = (list: number[], isAdd = false) =>
    list.map((v) => (
        <MenuItem key={v} value={v}>
            {isAdd && v > 0 ? `+${v}` : v}
        </MenuItem>
    ));

const initialForm: ApiPayload = {
    userid: '',

    ODsphereRx: '',
    ODcylinderRx: '',
    ODaxis: '',
    ODaddPower: '',
    ODreadingPresciption: '',

    OSsphereRx: '',
    OScylinderRx: '',
    OSaxis: '',
    OSaddPower: '',
    OSreadingPresciption: '',

    age: '',
    gender: '',

    ODhvid: '',
    OShvid: '',
    ODksteep: '',
    OSksteep: '',
    ODkflat: '',
    OSkflat: '',
    ODpupilSize: '',
    OSpupilSize: '',

    AEdryEyeIssue: '',
    AEIrregularCornea: '',
    AErce: '',
    AEother: '',

    finalMFlensPurchased: '',
    finalMFlensPurchasedNotes: '',
    finalMFPower: '',
    finalMFPowerNotes: '',
    CLs: '',
    CLNotes: '',

    existingMFpurchased: '',
    existedMFpurchasedNotes: '',
    existedMFPower: '',
    existedMFPowerNotes: '',

    newLensTriedBeforePurchasing: '',
    newLensTriedBeforePurchasingNotes: '',

    tryDifferentLenses: '',
    tryDifferentLensesNotes: '',

    priorRefractiveSurgery: '',
    priorRefractiveSurgeryNotes: '',
    priorCataractSurgery: '',
    priorCataractSurgeryNotes: '',

    OTCeyeProducts: '',
    hoursWearingCLs: '',
    typeApersonality: '',
    postFitSatisfaction: '',
};

// --------------------------- Component ---------------------------
const StandardCare: React.FC = () => {
    const { userid } = useParams<{ userid: string }>();
    const { showSuccess, showError } = useSnackbar();
    const [data, setData] = useState<ApiPayload>({
        ...initialForm,
        userid: userid ?? '',
    });
    const [submitting, setSubmitting] = useState(false);

    // If route param changes while mounted, keep payload in sync
    React.useEffect(() => {
        if (userid && data.userid !== userid) {
            setData((d) => ({ ...d, userid }));
        }
    }, [userid]);

    // Field setters
    const setField = <K extends keyof ApiPayload>(key: K, value: ApiPayload[K]) =>
        setData((d) => ({ ...d, [key]: value }));

    // Minimal validation that prevents accidental empty submit
    const isValid = useMemo(() => {
        // Check if any field is filled
        return Object.entries(data).some(([key, value]) => {
            if (key === 'userid') return false; // Exclude userid from check
            if (typeof value === 'string') return value.trim() !== '';
            if (typeof value === 'number') return true;
            return false;
        });
    }, [data]);

    const handleSubmit = async () => {
        if (!isValid) return;
        setSubmitting(true);
        try {
            await AxiosInstance.post(`/patients/standard-care-info?call=webapp&userid=${userid}`, data);
            showSuccess('Standard care information saved successfully!');
        } catch (err) {
            console.error(err);
            showError('Failed to save standard care information. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const fetchStandardCareInfo = async () => {
        if (!userid) return;
        try {
            const response = await AxiosInstance.get(`/patients/standard-care-info?call=webapp&userid=${userid}`);
            if (response.data) {
                setData({ ...initialForm, ...response.data, userid });
            }
        } catch (err) {
            console.error('Error fetching standard care info:', err);
            // Optionally show error message
            // showError('Failed to load standard care information.');
        }
    };

    useEffect(() => {
        fetchStandardCareInfo();
    }, [userid]);

    const { oktaAuth } = useOktaAuth();
    const [activeTab, setActiveTab] = useState("patients");
    const location = useLocation();

    useEffect(() => {
        if (location.pathname === "/patients") {
                setActiveTab("patients");
        }
    }, [location.pathname]);
    
    const handleTabChange = (tab: string) => {
            setActiveTab(tab);
    };

    return (
        <Box
                                sx={{
                                        minHeight: "100vh",
                                        bgcolor: "#f9fbfd",
                                        display: "flex",
                                        flexDirection: "column",
                                }}
                        >
                                <Header activeTab={activeTab} onTabChange={handleTabChange} onLogout={() => oktaAuth.signOut()} />
        
                                <Container
                                        maxWidth="xl"
                                        sx={{
                                                flexGrow: 1,
                                                py: 4,
                                                px: { xs: 2, md: 4 },
                                        }}
                                >
        <Paper elevation={0} sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ mb: 1 }}>
                Standard care information
            </Typography>
            {!userid && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    No <strong>userid</strong> found in route params. Navigate to a URL like <code>/patients/&lt;userid&gt;/form</code>.
                </Alert>
            )}

            {/* Rx: OD + OS */}
            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Latest Rx</Typography>
            <Grid container spacing={2}>
                {/* OD */}
                <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="subtitle1" sx={{ mb: 1 }}>OD</Typography>

                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel>Sphere Rx</InputLabel>
                            <Select
                                label="Sphere Rx"
                                value={data.ODsphereRx === '' ? '' : data.ODsphereRx}
                                onChange={(e) => setField('ODsphereRx', Number(e.target.value))}
                            >
                                {renderNumberMenuItems(LENS_POWERS)}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel>Cylinder Rx</InputLabel>
                            <Select
                                label="Cylinder Rx"
                                value={data.ODcylinderRx === '' ? '' : data.ODcylinderRx}
                                onChange={(e) => setField('ODcylinderRx', Number(e.target.value))}
                            >
                                {renderNumberMenuItems(CYLINDER_POWERS)}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel>Axis</InputLabel>
                            <Select
                                label="Axis"
                                value={data.ODaxis === '' ? '' : data.ODaxis}
                                onChange={(e) => setField('ODaxis', Number(e.target.value))}
                            >
                                {AXIS_OPTIONS.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel>Add Power</InputLabel>
                            <Select
                                label="Add Power"
                                value={data.ODaddPower === '' ? '' : data.ODaddPower}
                                onChange={(e) => setField('ODaddPower', Number(e.target.value))}
                            >
                                {renderNumberMenuItems(ADD_POWERS, true)}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth>
                            <InputLabel>Reading prescription</InputLabel>
                            <Select
                                label="Reading prescription"
                                value={data.ODreadingPresciption === '' ? '' : data.ODreadingPresciption}
                                onChange={(e) => setField('ODreadingPresciption', Number(e.target.value))}
                            >
                                {renderNumberMenuItems(ADD_POWERS, true)}
                            </Select>
                        </FormControl>
                    </Paper>
                </Grid>

                {/* OS */}
                <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="subtitle1" sx={{ mb: 1 }}>OS</Typography>

                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel>Sphere Rx</InputLabel>
                            <Select
                                label="Sphere Rx"
                                value={data.OSsphereRx === '' ? '' : data.OSsphereRx}
                                onChange={(e) => setField('OSsphereRx', Number(e.target.value))}
                            >
                                {renderNumberMenuItems(LENS_POWERS)}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel>Cylinder Rx</InputLabel>
                            <Select
                                label="Cylinder Rx"
                                value={data.OScylinderRx === '' ? '' : data.OScylinderRx}
                                onChange={(e) => setField('OScylinderRx', Number(e.target.value))}
                            >
                                {renderNumberMenuItems(CYLINDER_POWERS)}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel>Axis</InputLabel>
                            <Select
                                label="Axis"
                                value={data.OSaxis === '' ? '' : data.OSaxis}
                                onChange={(e) => setField('OSaxis', Number(e.target.value))}
                            >
                                {AXIS_OPTIONS.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel>Add Power</InputLabel>
                            <Select
                                label="Add Power"
                                value={data.OSaddPower === '' ? '' : data.OSaddPower}
                                onChange={(e) => setField('OSaddPower', Number(e.target.value))}
                            >
                                {renderNumberMenuItems(ADD_POWERS, true)}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth>
                            <InputLabel>Reading prescription</InputLabel>
                            <Select
                                label="Reading prescription"
                                value={data.OSreadingPresciption === '' ? '' : data.OSreadingPresciption}
                                onChange={(e) => setField('OSreadingPresciption', Number(e.target.value))}
                            >
                                {renderNumberMenuItems(ADD_POWERS, true)}
                            </Select>
                        </FormControl>
                    </Paper>
                </Grid>
            </Grid>

            {/* Demographics */}
            <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>Demographics</Typography>
            <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                    <TextField
                        label="Age"
                        type="number"
                        value={data.age}
                        onChange={(e) => setField('age', e.target.value === '' ? '' : Number(e.target.value))}
                        fullWidth
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <FormControl fullWidth>
                        <InputLabel>Gender</InputLabel>
                        <Select
                            label="Gender"
                            value={data.gender}
                            onChange={(e) => setField('gender', e.target.value as Gender)}
                        >
                            {GENDER.map((g) => <MenuItem key={g} value={g}>{g}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <FormControl fullWidth>
                        <InputLabel>OTC eye products</InputLabel>
                        <Select
                            label="OTC eye products"
                            value={data.OTCeyeProducts}
                            onChange={(e) => setField('OTCeyeProducts', e.target.value as YesNo)}
                        >
                            {YES_NO.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>

            {/* OD/OS measurements */}
            <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>Measurements (OD / OS)</Typography>
            <Grid container spacing={2}>
                <Grid item xs={12} sm={3}>
                    <TextField
                        label="OD HVID (mm)"
                        type="number"
                        value={data.ODhvid}
                        onChange={(e) => setField('ODhvid', e.target.value === '' ? '' : Number(e.target.value))}
                        fullWidth
                    />
                </Grid>
                <Grid item xs={12} sm={3}>
                    <TextField
                        label="OS HVID (mm)"
                        type="number"
                        value={data.OShvid}
                        onChange={(e) => setField('OShvid', e.target.value === '' ? '' : Number(e.target.value))}
                        fullWidth
                    />
                </Grid>
                <Grid item xs={12} sm={3}>
                    <TextField
                        label="OD Pupil Size (mm)"
                        type="number"
                        value={data.ODpupilSize}
                        onChange={(e) => setField('ODpupilSize', e.target.value === '' ? '' : Number(e.target.value))}
                        fullWidth
                    />
                </Grid>
                <Grid item xs={12} sm={3}>
                    <TextField
                        label="OS Pupil Size (mm)"
                        type="number"
                        value={data.OSpupilSize}
                        onChange={(e) => setField('OSpupilSize', e.target.value === '' ? '' : Number(e.target.value))}
                        fullWidth
                    />
                </Grid>

                <Grid item xs={12} sm={6}>
                    <TextField
                        label="OD K-Steep (e.g., 45.25@084)"
                        value={data.ODksteep}
                        onChange={(e) => setField('ODksteep', e.target.value)}
                        fullWidth
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        label="OS K-Steep (e.g., 45.75@110)"
                        value={data.OSksteep}
                        onChange={(e) => setField('OSksteep', e.target.value)}
                        fullWidth
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        label="OD K-Flat (e.g., 44.75@174)"
                        value={data.ODkflat}
                        onChange={(e) => setField('ODkflat', e.target.value)}
                        fullWidth
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        label="OS K-Flat (e.g., 45.25@020)"
                        value={data.OSkflat}
                        onChange={(e) => setField('OSkflat', e.target.value)}
                        fullWidth
                    />
                </Grid>
            </Grid>

            {/* Anterior eye & corneal indicators */}
            <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>Anterior / Corneal Indicators</Typography>
            <Grid container spacing={2}>
                {([
                    { key: 'AEdryEyeIssue', label: 'Dry eye indicators' },
                    { key: 'AEIrregularCornea', label: 'Irregular cornea / high aberrations' },
                    { key: 'AErce', label: 'Recurrent Corneal Erosion (RCE)' },
                ] as const).map(({ key, label }) => (
                    <Grid item xs={12} sm={4} key={key}>
                        <FormControl fullWidth>
                            <InputLabel>{label}</InputLabel>
                            <Select
                                label={label}
                                value={data[key]}
                                onChange={(e) => setField(key, e.target.value as Severity)}
                            >
                                {SEVERITY.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                ))}
                <Grid item xs={12}>
                    <TextField
                        label="Other"
                        value={data.AEother}
                        onChange={(e) => setField('AEother', e.target.value)}
                        fullWidth
                        multiline
                    />
                </Grid>
            </Grid>

            {/* Final MF & CLs */}
            <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>Purchases</Typography>
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
            <InputLabel>Final Multifocal Lens purchased</InputLabel>
            <Select
              label="Final Multifocal Lens purchased"
              value={data.finalMFlensPurchased}
              onChange={(e) => {
                const value = e.target.value;
                setField('finalMFlensPurchased', value);
                if (value === 'NONE') {
                  setField('finalMFPower', '');
                }
              }}
            >
              {MULTIFOCAL_CLS.map((lens) => (
                <MenuItem key={lens} value={lens}>{lens}</MenuItem>
              ))}
            </Select>
          </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        label="Notes"
                        value={data.finalMFlensPurchasedNotes}
                        onChange={(e) => setField('finalMFlensPurchasedNotes', e.target.value)}
                        fullWidth
                    />
                </Grid>

                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth disabled={data.finalMFlensPurchased === 'NONE'}>
                        <InputLabel>Add Power</InputLabel>
                        <Select
                            label="Add Power"
                            value={data.finalMFPower}
                            onChange={(e) => setField('finalMFPower', e.target.value as AddPower)}
                        >
                            {ADD_POWER.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        label="Notes"
                        value={data.finalMFPowerNotes}
                        onChange={(e) => setField('finalMFPowerNotes', e.target.value)}
                        fullWidth
                    />
                </Grid>

                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                        <InputLabel>Non‑Multifocal CLs</InputLabel>
                        <Select
                            label="Non‑Multifocal CLs"
                            value={data.CLs}
                            onChange={(e) => setField('CLs', e.target.value)}
                        >
                            {NON_MULTIFOCAL_CLS.map((opt) => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        label="Notes"
                        value={data.CLNotes}
                        onChange={(e) => setField('CLNotes', e.target.value)}
                        fullWidth
                    />
                </Grid>
            </Grid>

            {/* Existing MF wearers */}
            <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>Existing Multifocal Wearers</Typography>
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
            <InputLabel>Past MF purchased</InputLabel>
            <Select
              label="Past MF purchased"
              value={data.existingMFpurchased}
              onChange={(e) => {
                const value = e.target.value;
                setField('existingMFpurchased', value);
                if (value === 'NONE') {
                  setField('existedMFPower', '');
                }
              }}
            >
              {MULTIFOCAL_CLS.map((lens) => (
                <MenuItem key={lens} value={lens}>{lens}</MenuItem>
              ))}
            </Select>
          </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        label="Notes"
                        value={data.existedMFpurchasedNotes}
                        onChange={(e) => setField('existedMFpurchasedNotes', e.target.value)}
                        fullWidth
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth disabled={data.existingMFpurchased === 'NONE'}>
                        <InputLabel>Past Add Power</InputLabel>
                        <Select
                            label="Past Add Power"
                            value={data.existedMFPower}
                            onChange={(e) => setField('existedMFPower', e.target.value as AddPower)}
                        >
                            {ADD_POWER.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        label="Notes"
                        value={data.existedMFPowerNotes}
                        onChange={(e) => setField('existedMFPowerNotes', e.target.value)}
                        fullWidth
                    />
                </Grid>
            </Grid>

            {/* New multifocal wearers */}
            <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>New Multifocal Wearers</Typography>
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
            <InputLabel>How many lens pairs are fitted for prescription or ADD adjustments?</InputLabel>
            <Select
              label="How many lens pairs are fitted for prescription or ADD adjustments?"
              value={data.newLensTriedBeforePurchasing === '' ? '' : data.newLensTriedBeforePurchasing}
              onChange={(e) => setField('newLensTriedBeforePurchasing', Number(e.target.value))}
            >
              {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((v) => (
                <MenuItem key={v} value={v}>{v}</MenuItem>
              ))}
            </Select>
          </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        label="Notes"
                        value={data.newLensTriedBeforePurchasingNotes}
                        onChange={(e) => setField('newLensTriedBeforePurchasingNotes', e.target.value)}
                        fullWidth
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Is it necessary to try lenses from different brands during the fitting process?</InputLabel>
                    <Select
                      label="Is it necessary to try lenses from different brands during the fitting process?"
                      value={data.tryDifferentLenses === '' ? '' : data.tryDifferentLenses}
                      onChange={(e) => setField('tryDifferentLenses', e.target.value as YesNo)}
                    >
                      {YES_NO.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                      label="Notes"
                      value={data.tryDifferentLensesNotes}
                      onChange={(e) => setField('tryDifferentLensesNotes', e.target.value)}
                      fullWidth
                  />
                </Grid>
            </Grid>

            {/* Lifestyle / personality / post-fit */}
            <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>Lifestyle & Post‑fit</Typography>
            <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                    <TextField
                        label="Hours wearing CLs / day"
                        type="number"
                        value={data.hoursWearingCLs}
                        onChange={(e) =>
                            setField('hoursWearingCLs', e.target.value === '' ? '' : Number(e.target.value))
                        }
                        fullWidth
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <FormControl fullWidth>
                        <InputLabel>Type A personality</InputLabel>
                        <Select
                            label="Type A personality"
                            value={data.typeApersonality}
                            onChange={(e) => setField('typeApersonality', e.target.value as YesNo)}
                        >
                            {YES_NO.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <FormControl fullWidth>
            <InputLabel>Post‑fit satisfaction (1–5)</InputLabel>
            <Select
              label="Post‑fit satisfaction (1–5)"
              value={data.postFitSatisfaction === '' ? '' : data.postFitSatisfaction}
              onChange={(e) => setField('postFitSatisfaction', Number(e.target.value))}
            >
              <MenuItem value={1}>1 - Not Satisfied</MenuItem>
              <MenuItem value={2}>2</MenuItem>
              <MenuItem value={3}>3</MenuItem>
              <MenuItem value={4}>4</MenuItem>
              <MenuItem value={5}>5 - Extremely Satisfied</MenuItem>
            </Select>
          </FormControl>
                </Grid>
            </Grid>

            {/* Submit */}
            <Divider sx={{ my: 3 }} />
            <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={!isValid || submitting}
                >
                    {submitting ? 'Saving…' : 'Save'}
                </Button>
                <Button
                    variant="outlined"
                    onClick={() => setData({ ...initialForm, userid: userid ?? '' })}
                    disabled={submitting}
                >
                    Reset
                </Button>
            </Box>
        </Paper>
        </Container>
        <Footer />
                        </Box>
    );
};

export default StandardCare;
